import {Control} from "react-hook-form";
import {FormFieldInputs, InputProps} from "../components/input/Input";
import {BaseExtraAttribute, ExtraAttributeType} from "../models/extra-attributes.model";

/**
 * Converts extraAttributes into a structure suitable for your custom Input components.
 * Includes handling of min/max constraints based on the attribute type.
 */
export const convertExtraAttributesToFormFields = (
    extraAttributes: (BaseExtraAttribute & { value: string | null })[],
    control?: Control
): FormFieldInputs => {
    const formFields: FormFieldInputs = {};

    if (!extraAttributes || !Array.isArray(extraAttributes)) {
        return formFields;
    }

    extraAttributes.forEach((attr) => {
        const fieldName = `extra_attribute_${attr.id}`;

        const field: InputProps = {
            name: fieldName,
            label: attr.label,
            helpText: attr.helpText || "",
            type: mapAttributeTypeToFieldType(attr.type),
            isDisabled: false, // Adjust if needed
            rules: { required: attr.required ? `${attr.label} is required` : false },
            control: control,
        };

        // 1. Handle default value — preserve the value's type. Only numeric fields
        //    become numbers; parseInt on a date ('2026-01-15' → 2026) or text default
        //    silently mangles it.
        if (attr.value !== undefined && attr.value !== null) {
            field.rules.defaultValue =
                attr.type === ExtraAttributeType.NUMBER ? parseInt(`${attr.value}`) : `${attr.value}`;
        }

        // 2. Handle min/max constraints
        //    - For text fields, we'll use `maxLength`.
        //    - For number/date, enforce the bounds via RHF `min`/`max` rules (the API
        //      only enforces min/max for TEXT/URL/NUMBER, so admin-set date ranges are
        //      otherwise validated nowhere).
        if (attr.type === ExtraAttributeType.TEXT) {
            // Text
            if (attr.maximum) {
                field.maximum = parseInt(`${attr.maximum}`); // Typically used as HTML `maxLength`
            }
        } else if (attr.type === ExtraAttributeType.NUMBER) {
            if (attr.minimum !== null && attr.minimum !== undefined) {
                const min = parseInt(`${attr.minimum}`);
                field.minimum = min;
                field.rules.min = { value: min, message: `${attr.label} must be at least ${min}` };
            }
            if (attr.maximum !== null && attr.maximum !== undefined) {
                const max = parseInt(`${attr.maximum}`);
                field.maximum = max;
                field.rules.max = { value: max, message: `${attr.label} must be at most ${max}` };
            }
        } else if (attr.type === ExtraAttributeType.DATE) {
            // Date bounds are ISO strings ('2026-01-15'); keep them as strings so RHF's
            // date comparison enforces them, instead of parseInt collapsing them to a year.
            if (attr.minimum !== null && attr.minimum !== undefined) {
                const min = `${attr.minimum}`;
                field.rules.min = { value: min, message: `${attr.label} must be on or after ${min}` };
            }
            if (attr.maximum !== null && attr.maximum !== undefined) {
                const max = `${attr.maximum}`;
                field.rules.max = { value: max, message: `${attr.label} must be on or before ${max}` };
            }
        }

        // 3. Handle Dropdown options (type = 2)
        if (attr.type === ExtraAttributeType.DROPDOWN && attr.options) {
            field.options = attr.options.map((option) => ({
                key: option.id.toString(),
                text: option.option,
            }));
        }

        // Add the field config to our formFields
        formFields[fieldName] = field;
    });

    return formFields;
};

// Helper to map attribute types to input field types
const mapAttributeTypeToFieldType = (
    attributeType: number
): InputProps["type"] => {
    switch (attributeType) {
        case ExtraAttributeType.TEXT: // Text
            return "text";
        case ExtraAttributeType.DROPDOWN: // Dropdown
            return "select";
        case ExtraAttributeType.CHECKBOX: // Checkbox
            return "checkbox";
        case ExtraAttributeType.SPACER: // Spacer (not a form field, maybe a render function)
            return "textarea";
        case ExtraAttributeType.URL: // URL
            return "url";
        case ExtraAttributeType.NUMBER: // Number
            return "number";
        case ExtraAttributeType.DATE: // Date
            return "date";
        default:
            return "text";
    }
};
